import {
  InstanceFlavor,
  InstanceImage,
  InstanceImageCategory,
  InstanceImageGroup,
  InstanceImageGroups,
  InstanceModelCategory,
  InstanceSnapshot,
} from "../../../types/instance";
import { capitalizeFirstLetter, spliter } from "../../../utils";

import { ReactComponent as LinuxIcon } from "./../../../assets/icons/docker-images/linux.svg";
import { ReactComponent as CentOSIcon } from "./../../../assets/icons/docker-images/centos.svg";
import { ReactComponent as DebianIcon } from "./../../../assets/icons/docker-images/debian.svg";
import { ReactComponent as FedoraIcon } from "./../../../assets/icons/docker-images/fedora.svg";
import { ReactComponent as UbuntuIcon } from "./../../../assets/icons/docker-images/ubuntu.svg";
import { ReactComponent as WindowsIcon } from "./../../../assets/icons/docker-images/windows.svg";
import { ReactComponent as CPanelIcon } from "./../../../assets/icons/docker-images/cpanel.svg";
import { ReactComponent as JupyterIcon } from "./../../../assets/icons/docker-images/jupyter.svg";
import { ReactComponent as DataikuIcon } from "./../../../assets/icons/docker-images/dataiku.svg";
import { ReactComponent as DockerIcon } from "./../../../assets/icons/socials/docker-color.svg";
import { ReactComponent as KubernetesIcon } from "./../../../assets/icons/docker-images/kubernetes.svg";
import { ReactComponent as PleskIcon } from "./../../../assets/icons/docker-images/plesk.svg";
import { ReactComponent as NvidiaIcon } from "./../../../assets/icons/docker-images/nvidia.svg";
import { ReactComponent as EmptyIcon } from "./../../../assets/icons/computer-desktop.svg";
import { DBSCatalog } from "../../../types/database";
import { getServicesZoneGroup, ZoneGroupKey } from "../../../utils/countries";

const modelCategories: Array<InstanceModelCategory> = [
  {
    title: "General Purpose",
    keys: ["b3", "b2"],
    description:
      "Instances for general use offer a good balance of RAM and performance.",
    flavors: [],
  },
  {
    title: "Compute Optimized",
    keys: ["c3", "c2"],
    description:
      "Instances with optimised computing are ideally suited to applications that require high processing speeds or parallel task processing.",
    flavors: [],
  },
  {
    title: "Memory Optimized",
    keys: ["r3", "r2"],
    description:
      "Instances with optimised memory are recommended for your databases, analyses and in-memory computing, as well as other RAM-intensive applications.",
    flavors: [],
  },
  {
    title: "GPU",
    keys: ["l4", "l40s", "h100", "t1-le", "t1", "t2-le", "t2"],
    description:
      "Instances with accelerated computing (GPU, FPGA) are up to 1,000 times faster than a CPU for certain applications (rendering, video transcoding, bioinformatics, big data, deep learning, etc.)",
    flavors: [],
  },
  {
    title: "Discovery",
    keys: ["d2"],
    description:
      "Instances with shared resources (Discovery) are adapted for tests, recipes and development environments. Their performance may vary slightly over time.",
    flavors: [],
  },
  {
    title: "Storage Optimized",
    keys: ["i1"],
    description:
      "IOPS instances deliver the fastest disk transactions in the Public Cloud range.",
    flavors: [],
  },
  {
    title: "Metal",
    keys: ["bm"],
    description:
      "Metal instances offer physical servers on demand. They are delivered in a few minutes, and can be billed on an hourly or monthly basis.",
    flavors: [],
  },
];

export const makeModels = (
  flavors: Array<InstanceFlavor>,
  region: string | undefined
) => {
  //filter flex items //
  let filteredFlavors = flavors.filter((flavor) => {
    const isFlex = flavor.name.endsWith("-flex");
    return !isFlex;
  });

  //filter if we have region
  if (region) {
    filteredFlavors = filteredFlavors.filter(
      (flavor) => flavor.region === region
    );
  }

  for (const category of modelCategories) {
    for (const key of category.keys) {
      const _filteredFlavors = filteredFlavors.filter((flavor) => {
        return flavor.name.includes(key);
      });
      category.flavors = [...category.flavors, ..._filteredFlavors];
    }
  }
  return modelCategories;
};

export const getModelIndexByFlavor = (flavor: InstanceFlavor) => {
  let modelItemIndex = 0;
  modelCategories.forEach((cat, i) => {
    cat.keys.forEach((key) => {
      if (flavor.name.startsWith(key)) {
        modelItemIndex = i;
      }
    });
  });
  return modelItemIndex;
};

export const makeModelGroup = (
  flavors: Array<InstanceFlavor>,
  keys: string[]
) => {
  const endFlavors: Array<InstanceFlavor> = [];
  for (const key of keys) {
    //filter flavors by key
    const filteredFlavors = flavors.filter((flavor) => {
      return flavor.name.startsWith(key);
    });

    //generate group keys
    const allGroupKeys = filteredFlavors
      .map((ff) => ff.name)
      .map((n) => n.split("-").slice(1).join("-"));
    const uniqueGroupKeys = new Set(allGroupKeys);

    for (const groupKey of Array.from(uniqueGroupKeys)) {
      const flavor = flavors.find((f) =>
        f.name.startsWith(`${key}-${groupKey}`)
      );
      if (flavor) endFlavors.push(flavor);
    }
  }
  return endFlavors;
};

export const instanceModelgroupCompatibleCatalogs = (
  catalogs: DBSCatalog[],
  startsWith: string
) => {
  const availableCatalogs = catalogs.filter((c) =>
    c.planCode.startsWith(startsWith)
  );

  const instanceZoneGroups: Record<
    ZoneGroupKey,
    {
      groupKey: ZoneGroupKey;
      groupName: string;
      catalog: DBSCatalog | null;
    }
  > = {
    "1AZ": { groupKey: "1AZ", groupName: "1-AZ", catalog: null },
    "3AZ": { groupKey: "3AZ", groupName: "3-AZ", catalog: null },
    LZ: {
      groupKey: "LZ",
      groupName: "Local Zone",
      catalog: null,
    },
    Other: {
      groupKey: "Other",
      groupName: "Other",
      catalog: null,
    },
  };

  for (const catalog of availableCatalogs) {
    const zone = getServicesZoneGroup(catalog.planCode, startsWith);
    instanceZoneGroups[zone].catalog = catalog; // Overwrite to always keep last one
  }

  const grouped = Object.values(instanceZoneGroups).filter(
    (g) => g.catalog !== null
  );

  const targetCatalog = instanceZoneGroups["1AZ"].catalog ?? null;

  return { groupedCatalogs: grouped, targetCatalog };
};

export const findLastTrueAddon = (catalogs: DBSCatalog[]) => {
  const addons = catalogs.filter(
    (a) => a.pricings && a.pricings.length > 0 && a.pricings[0].price > 0.01
  );
  return addons.at(-1);
};

export const makeImagesCategory = (
  images: Array<InstanceImage>,
  snapshots: Array<InstanceSnapshot>
) => {
  const apps = images.filter(
    (image) => image.tags.length > 0 && image.tags.includes("application")
  );

  const windowsOperationSystems = images
    .filter((image) => image.type === "windows")
    .filter((image) => !apps.map((a) => a.id).includes(image.id));

  const unixDistributions = images
    .filter((image) => !apps.map((a) => a.id).includes(image.id))
    .filter(
      (image) => !windowsOperationSystems.map((w) => w.id).includes(image.id)
    );

  let imageCategories: Array<InstanceImageCategory> = [
    {
      id: "unix",
      title: "Unix distributions",
      keys: [],
      description:
        "To use the images, you will need to accept the supplier's user licence agreement.",
      images: unixDistributions,
    },
    {
      id: "windows",
      title: "Windows operating systems",
      keys: [],
      description:
        "To use the images, you will need to accept the supplier's user licence agreement.",
      images: windowsOperationSystems,
    },
    {
      id: "apps",
      title: "Operating system + Apps",
      keys: [],
      description: "",
      images: apps,
    },
  ];
  if (snapshots && snapshots.length) {
    imageCategories = [
      ...imageCategories,
      {
        id: "backups",
        title: "Backups",
        keys: [],
        description: "",
        images: snapshots,
      } as InstanceImageCategory,
    ];
  }
  return imageCategories;
};

export const makeUnixImagesGroup = (
  images: Array<InstanceImage>,
  isBaremetal: boolean = false
): InstanceImageGroups => {
  const baremetalOptions = images.filter((i) => i.type === "baremetal-linux");
  const otherOptions = images.filter(
    (i) => !baremetalOptions.map((o) => o.id).includes(i.id)
  );

  const unixImageGroups: InstanceImageGroup[] = [
    {
      id: "linux",
      title: "Linux",
      imageIcon: getImageIcon("linux"),
      users: ["almalinux", "rocky"],
      images: [],
    },
    {
      id: "centos",
      title: "CentOS",
      imageIcon: getImageIcon("centos"),
      users: ["centos"],
      images: [],
    },
    {
      id: "arclinux",
      title: "Arc Linux",
      imageIcon: getImageIcon("linux"),
      users: ["cloudlinux"],
      images: [],
    },
    {
      id: "debian",
      title: "Debian",
      imageIcon: getImageIcon("debian"),
      users: ["debian"],
      images: [],
    },
    {
      id: "fedora",
      title: "Fedora",
      imageIcon: getImageIcon("fedora"),
      users: ["fedora"],
      images: [],
    },
    {
      id: "ubuntu",
      title: "Ubuntu",
      imageIcon: getImageIcon("ubuntu"),
      users: ["ubuntu"],
      images: [],
    },
  ];

  const unixBaremetaImageGroups: InstanceImageGroup[] = [
    {
      id: "debian-baremeta",
      title: "Debian",
      imageIcon: getImageIcon("debian"),
      users: ["debian"],
      images: [],
    },
    {
      id: "ubuntu-baremeta",
      title: "Ubuntu",
      imageIcon: getImageIcon("ubuntu"),
      users: ["ubuntu"],
      images: [],
    },
  ];

  for (const group of unixImageGroups) {
    for (const user of group.users) {
      const _filteredImages = otherOptions.filter((i) => i.user === user);
      group.images = [...group.images, ..._filteredImages];
    }
  }

  for (const group of unixBaremetaImageGroups) {
    for (const user of group.users) {
      const _filteredImages = baremetalOptions.filter((i) => i.user === user);
      group.images = [...group.images, ..._filteredImages];
    }
  }
  let avalibleImagesGroup: InstanceImageGroup[] = [];
  let unavalibleImagesGroup: InstanceImageGroup[] = [];
  if (!isBaremetal) {
    avalibleImagesGroup = unixImageGroups;
    unavalibleImagesGroup = unixBaremetaImageGroups;
  } else {
    avalibleImagesGroup = unixBaremetaImageGroups;
    unavalibleImagesGroup = unixImageGroups;
  }
  return { avalibleImagesGroup, unavalibleImagesGroup };
};

export const makeWindowsImagesGroup = (
  images: Array<InstanceImage>,
  flavorName: string
): InstanceImageGroups => {
  // - UEFI
  const selectedFlavorType = getFlavorType(flavorName);
  const avalibleDesktopFlavorTypes = ["b2", "c2", "r2", "t1", "t2", "i1"];

  //2016
  const windows2016 = images.filter((i) =>
    i.name.includes("Windows Server 2016")
  );
  const windows2016Main = windows2016.filter((i) => !i.name.endsWith("UEFI"));
  const windows2016UEFI = windows2016.filter((i) => i.name.endsWith("UEFI"));

  //others
  const otherWindows = images.filter(
    (i) => !windows2016.map((w) => w.id).includes(i.id)
  );
  const otherWindowsMain = otherWindows.filter((i) => !i.name.endsWith("UEFI"));
  const otherWindowsUEFI = otherWindows.filter((i) => i.name.endsWith("UEFI"));

  const otherWindowsGroupOption = (
    id: string,
    images: InstanceImage[]
  ): InstanceImageGroup => ({
    id,
    title: "Other Windows operating systems",
    imageIcon: getImageIcon("windows"),
    users: ["others"],
    images,
  });

  const windows2016GroupOption = (
    id: string,
    images: InstanceImage[]
  ): InstanceImageGroup => ({
    id,
    title: "Windows Server 2016",
    imageIcon: getImageIcon("windows"),
    users: ["2016"],
    images,
  });

  let avalibleImagesGroup: InstanceImageGroup[] = [];
  let unavalibleImagesGroup: InstanceImageGroup[] = [];

  if (otherWindowsMain.length) {
    const option = otherWindowsGroupOption("windows-other", otherWindowsMain);
    if (avalibleDesktopFlavorTypes.includes(selectedFlavorType)) {
      avalibleImagesGroup.push(option);
    } else {
      unavalibleImagesGroup.push(option);
    }
  }

  if (windows2016Main.length) {
    const option = windows2016GroupOption("windows-2016", windows2016Main);
    if (avalibleDesktopFlavorTypes.includes(selectedFlavorType)) {
      avalibleImagesGroup.push(option);
    } else {
      unavalibleImagesGroup.push(option);
    }
  }

  if (otherWindowsUEFI.length) {
    const avaliblesImages: InstanceImage[] = [];
    const unavaliblesImages: InstanceImage[] = [];

    for (const i of otherWindowsUEFI) {
      if (i.flavorType?.includes(selectedFlavorType)) {
        avaliblesImages.push(i);
      } else {
        unavaliblesImages.push(i);
      }
    }

    if (avaliblesImages.length) {
      const group = avalibleImagesGroup.find((aig) =>
        aig.users.includes("others")
      );

      if (group) {
        avalibleImagesGroup = avalibleImagesGroup.map((g) => {
          if (g.users.includes("others")) {
            return { ...g, images: [...g.images, ...avaliblesImages] };
          }
          return g;
        });
      } else {
        const option = otherWindowsGroupOption(
          "windows-other-uefi",
          avaliblesImages
        );
        avalibleImagesGroup.push(option);
      }
    }

    if (unavaliblesImages.length) {
      const group = unavalibleImagesGroup.find((uaig) =>
        uaig.users.includes("others")
      );
      if (group) {
        unavalibleImagesGroup = unavalibleImagesGroup.map((g) => {
          if (g.users.includes("others")) {
            return { ...g, images: [...g.images, ...unavaliblesImages] };
          }
          return g;
        });
      } else {
        const option = otherWindowsGroupOption(
          "windows-other-uefi",
          unavaliblesImages
        );
        unavalibleImagesGroup.push(option);
      }
    }
  }

  if (windows2016UEFI.length) {
    const avaliblesImages: InstanceImage[] = [];
    const unavaliblesImages: InstanceImage[] = [];

    for (const i of windows2016UEFI) {
      if (i.flavorType?.includes(selectedFlavorType)) {
        avaliblesImages.push(i);
      } else {
        unavaliblesImages.push(i);
      }
    }

    if (avaliblesImages.length) {
      const group = avalibleImagesGroup.find((aig) =>
        aig.users.includes("2016")
      );
      if (group) {
        avalibleImagesGroup = avalibleImagesGroup.map((g) => {
          if (g.users.includes("2016")) {
            return { ...g, images: [...g.images, ...avaliblesImages] };
          }
          return g;
        });
      } else {
        const option = windows2016GroupOption(
          "windows-2016-uefi",
          avaliblesImages
        );
        avalibleImagesGroup.push(option);
      }
    }

    if (unavaliblesImages.length) {
      const group = unavalibleImagesGroup.find((uaig) =>
        uaig.users.includes("2016")
      );
      if (group) {
        unavalibleImagesGroup = unavalibleImagesGroup.map((g) => {
          if (g.users.includes("2016")) {
            return { ...g, images: [...g.images, ...unavaliblesImages] };
          }
          return g;
        });
      } else {
        const option = windows2016GroupOption(
          "windows-2016-uefi",
          unavaliblesImages
        );
        unavalibleImagesGroup.push(option);
      }
    }
  }

  return { avalibleImagesGroup, unavalibleImagesGroup };
};

export const makeAppsImagesGroup = (
  images: Array<InstanceImage>,
  isBaremetal: boolean = false
): InstanceImageGroups => {
  const optionObjectMaker = (
    id: string,
    title: string,
    images: Array<InstanceImage>,
    icon: React.ReactNode
  ) => ({
    id,
    title,
    imageIcon: icon,
    users: [],
    images,
  });

  let avalibleImagesGroup: InstanceImageGroup[] = [];
  let unavalibleImagesGroup: InstanceImageGroup[] = [];

  for (const image of images) {
    const nameArr = spliter(image.name, "-");
    const option = optionObjectMaker(
      image.id,
      nameArr.length > 1 ? nameArr[1] : nameArr[0],
      [image],
      getImageIcon(spliter(nameArr[1] || nameArr[0], " ")[0])
    );

    if (!isBaremetal) {
      avalibleImagesGroup.push(option);
    } else {
      unavalibleImagesGroup.push(option);
    }
  }
  return { avalibleImagesGroup, unavalibleImagesGroup };
};

export const makeBackupImagesGroup = (
  images: Array<InstanceImage>,
  region: string
): InstanceImageGroups => {
  let avalibleImagesGroup: InstanceImageGroup[] = [];
  let unavalibleImagesGroup: InstanceImageGroup[] = [];

  for (const image of images) {
    const imageData: InstanceImageGroup = {
      id: image.id,
      title: image.name,
      images: [image],
      imageIcon: "",
      users: [],
      type: "backups",
    };
    if (image.region === region) {
      avalibleImagesGroup.push(imageData);
    } else {
      unavalibleImagesGroup.push(imageData);
    }
  }
  return { avalibleImagesGroup, unavalibleImagesGroup };
};

const getImageIcon = (service: string) => {
  // console.log("service image:", service);
  switch (service) {
    case "cPanel":
      return <CPanelIcon className="w-8 h-8" />;
    case "DataScience":
      return <JupyterIcon className="w-7 h-7" />;
    case "Dataiku":
      return <DataikuIcon className="w-7 h-7" />;
    case "Docker":
      return <DockerIcon className="w-7 h-7" />;
    case "Minikube":
      return <KubernetesIcon className="w-7 h-7" />;
    case "Plesk":
      return <PleskIcon className="w-7 h-7" />;
    case "NVIDIA":
      return <NvidiaIcon className="w-7 h-7" />;

    case "windows":
      return <WindowsIcon className="w-7 h-7" />;

    case "linux":
      return <LinuxIcon className="w-7 h-7" />;
    case "centos":
      return <CentOSIcon className="w-7 h-7" />;
    case "debian":
      return <DebianIcon className="w-7 h-7" />;
    case "fedora":
      return <FedoraIcon className="w-7 h-7" />;
    case "ubuntu":
      return <UbuntuIcon className="w-7 h-7" />;

    default:
      return <EmptyIcon className="w-7 h-7" />;
  }
};

export const getModels = (flavors: Array<InstanceFlavor>, name: string) => {
  return flavors.filter((f) => f.name === name);
};

const getFlavorType = (flavorName: string) =>
  flavorName.split("-").slice(0, -1).join("-");

export const makeBillingPeriodsOptions = (flavor: InstanceFlavor) => {
  if (flavor) {
    return Object.keys(flavor.planCodes)
      .filter((key) => flavor.planCodes[key as keyof typeof flavor.planCodes])
      .filter((key) => key !== "license")
      .map((key) => {
        const value =
          flavor.planCodes[key as keyof typeof flavor.planCodes] || "";

        return {
          id: value,
          title: capitalizeFirstLetter(key),
          value: key,
          key: "." + value.split(".").slice(1).join(".") || "",
        };
      });
  } else {
    return [];
  }
};
